import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { ParcelPhotosViewer } from "../../parcel-photos/parcel-photos-viewer";

/**
 * Shipment detail-page wrapper around `ParcelPhotosViewer`. Renders
 * the standard surrounding card chrome (border, padding, "Parcel
 * photos" header) the other shipment cards use so this section
 * doesn't look out of place next to Notes / Items / Customs.
 *
 * The viewer itself returns `null` when there are no photos, which
 * makes the card render an empty body. We keep the card present even
 * when empty so the operator gets a clear "no photos captured" signal
 * by absence of thumbnails rather than the whole section vanishing.
 */
export const ParcelPhotosCard = memo(function ParcelPhotosCard({
  fulfillmentGroup,
}: {
  fulfillmentGroup: FulfillmentGroupData;
}) {
  const team = useRequiredContext(TeamContext);
  // Skip the card entirely when the merchant hasn't opted into
  // parcel verification photos — the viewer's empty-state would
  // surface a blank-titled section otherwise.
  if (!(team?.settings.outboundLabels.verifyParcelPhotos?.enabled ?? false)) {
    return null;
  }
  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      flexDirection="column"
      gap="3xl"
      p="3xl"
      radius="md"
      w="full"
    >
      <Text color="secondary" fontSize="sm" fontWeight="medium">
        Parcel photos
      </Text>
      <ParcelPhotosViewer fulfillmentGroupId={fulfillmentGroup._id} />
    </Flex>
  );
});
